import { useState, useMemo, useEffect } from 'react';

function AutoOptimizeDialog({ isOpen, onClose, onApply, analysis, pageImages }) {
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [sortBy, setSortBy] = useState('pageNumber'); // 'pageNumber', 'suggestion', 'currentInk', 'savings'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  // Get pages and summary (with defaults to avoid issues when closed)
  const pages = analysis?.pages || [];
  const summary = analysis?.summary || { avgSavings: 0, preserveCount: 0, optimizeCount: 0 };

  // Pre-select all pages when dialog opens or analysis changes
  useEffect(() => {
    if (isOpen && pages.length > 0) {
      setSelectedPages(new Set(pages.map(p => p.pageNumber)));
    }
  }, [isOpen, pages]);

  // Sort pages based on current sort settings
  const sortedPages = useMemo(() => {
    if (pages.length === 0) return [];

    const sorted = [...pages].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'pageNumber':
          comparison = a.pageNumber - b.pageNumber;
          break;
        case 'suggestion':
          // Preserve first, then Optimize
          comparison = a.suggestion.localeCompare(b.suggestion);
          break;
        case 'currentInk':
          comparison = a.currentInk - b.currentInk;
          break;
        case 'savings':
          comparison = a.estimatedSavings - b.estimatedSavings;
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [pages, sortBy, sortDirection]);

  // Toggle sort
  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  // Select all pages
  const selectAll = () => {
    setSelectedPages(new Set(pages.map(p => p.pageNumber)));
  };

  // Select none
  const selectNone = () => {
    setSelectedPages(new Set());
  };

  // Toggle all pages selection (legacy - keeping for checkbox)
  const toggleSelectAll = () => {
    if (selectedPages.size === pages.length) {
      setSelectedPages(new Set());
    } else {
      setSelectedPages(new Set(pages.map(p => p.pageNumber)));
    }
  };

  // Toggle individual page
  const togglePage = (pageNumber) => {
    const newSelected = new Set(selectedPages);
    if (newSelected.has(pageNumber)) {
      newSelected.delete(pageNumber);
    } else {
      newSelected.add(pageNumber);
    }
    setSelectedPages(newSelected);
  };

  // Apply optimizations to selected pages
  const handleApply = () => {
    const pagesToApply = pages.filter(p => selectedPages.has(p.pageNumber));
    onApply(pagesToApply);
    onClose();
  };

  // Early return after all hooks
  console.log('🔔 AutoOptimizeDialog render:', { isOpen, hasAnalysis: !!analysis, pagesCount: pages.length });
  if (!isOpen || !analysis) {
    console.log('🚫 AutoOptimizeDialog returning null:', { isOpen, hasAnalysis: !!analysis });
    return null;
  }
  console.log('✅ AutoOptimizeDialog rendering dialog');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
            Auto-Optimize Results
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Review suggested optimizations for ink savings
          </p>
        </div>

        {/* Summary Stats */}
        <div className="px-6 py-4 bg-indigo-50 dark:bg-indigo-900/30 border-b border-indigo-200 dark:border-indigo-700 flex-shrink-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 uppercase">Total Pages</p>
              <p className="text-2xl font-bold text-indigo-900 dark:text-indigo-200">{pages.length}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 uppercase">Avg Savings</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {summary.avgSavings}%
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 uppercase">Preserve</p>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {summary.preserveCount}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 uppercase">Optimize</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {summary.optimizeCount}
              </p>
            </div>
          </div>
        </div>

        {/* Sort and Selection Controls */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 flex-shrink-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            {/* Sort controls */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort by:</span>
              <div className="flex gap-1">
                <button
                  onClick={() => handleSort('pageNumber')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    sortBy === 'pageNumber'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  Page # {sortBy === 'pageNumber' && (sortDirection === 'asc' ? '↑' : '↓')}
                </button>
                <button
                  onClick={() => handleSort('suggestion')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    sortBy === 'suggestion'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  Type {sortBy === 'suggestion' && (sortDirection === 'asc' ? '↑' : '↓')}
                </button>
                <button
                  onClick={() => handleSort('currentInk')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    sortBy === 'currentInk'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  Current Ink {sortBy === 'currentInk' && (sortDirection === 'asc' ? '↑' : '↓')}
                </button>
                <button
                  onClick={() => handleSort('savings')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    sortBy === 'savings'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  Savings {sortBy === 'savings' && (sortDirection === 'asc' ? '↑' : '↓')}
                </button>
              </div>
            </div>

            {/* Select All/None buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={selectAll}
                className="px-3 py-1 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
              >
                Select All
              </button>
              <button
                onClick={selectNone}
                className="px-3 py-1 text-xs font-medium bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                Select None
              </button>
            </div>
          </div>
        </div>

        {/* Page List */}
        <div className="flex-1 overflow-auto px-6 py-4">
          <div className="space-y-2">

            {/* Individual Pages */}
            {sortedPages.map((page) => {
              const pageImage = pageImages?.[page.pageNumber];

              return (
                <label
                  key={page.pageNumber}
                  className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPages.has(page.pageNumber)
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
                      : 'border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-500'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedPages.has(page.pageNumber)}
                    onChange={() => togglePage(page.pageNumber)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 rounded mt-1 flex-shrink-0"
                  />

                  {/* Thumbnails */}
                  {pageImage && (
                    <div className="flex gap-2 flex-shrink-0">
                      {/* Original */}
                      <div className="flex flex-col items-center">
                        <span className="text-xs text-gray-500 dark:text-gray-400 mb-1">Original</span>
                        <img
                          src={pageImage.original}
                          alt={`Page ${page.pageNumber} original`}
                          className="w-16 h-20 object-contain border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                        />
                      </div>

                      {/* Preview (only for optimize) */}
                      {page.suggestion === 'optimize' && pageImage.preview && (
                        <>
                          <div className="flex items-center">
                            <span className="text-gray-400 dark:text-gray-500">→</span>
                          </div>
                          <div className="flex flex-col items-center">
                            <span className="text-xs text-gray-500 dark:text-gray-400 mb-1">Optimized</span>
                            <img
                              src={pageImage.preview}
                              alt={`Page ${page.pageNumber} preview`}
                              className="w-16 h-20 object-contain border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-800 dark:text-gray-100">
                        Page {page.pageNumber}
                      </span>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded ${
                          page.suggestion === 'preserve'
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
                        }`}
                      >
                        {page.suggestion === 'preserve' ? 'Preserve' : 'Optimize'}
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                      {page.reason}
                    </p>

                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-gray-500 dark:text-gray-400">
                        Current ink: {page.currentInk}%
                      </span>
                      {page.suggestion === 'optimize' && (
                        <>
                          <span className="text-gray-500 dark:text-gray-400">→</span>
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            Savings: {page.estimatedSavings}%
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center flex-shrink-0">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {selectedPages.size} of {pages.length} pages selected
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={selectedPages.size === 0}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply to {selectedPages.size} Page{selectedPages.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AutoOptimizeDialog;
